import { retry } from '@change-corgi/core/http';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	PetitionDetailsMediaLinkPreview,
	type PetitionDetailsMediaLinkPreviewQuery,
	type PetitionDetailsMediaLinkPreviewQueryVariables,
} from './index.graphql';

type Success = Extract<
	PetitionDetailsMediaLinkPreviewQuery['mediaLinkPreview'],
	{ __typename: 'MediaLinkPreviewSuccess' }
>;

/**
 * The fields the Change-owned media hit card renders. Derived from the query rather than hand-written
 * so the shape stays pinned to the schema.
 */
export type MediaLinkPreview = Success['preview'];

/**
 * Fetches the metadata behind a media hit — headline, outlet, logo, thumbnail, publish date.
 *
 * Backed by iframely's `/api/iframely` endpoint, which carries the outlet logo and publish date that
 * the `/api/oembed` call behind `getIframelyData` does not.
 *
 * @throws when the preview can't be resolved (dead link, unsupported host, network failure).
 */
export async function getMediaLinkPreview(
	{ url }: { url: string },
	{ gql: { fetch } }: UtilityContext,
): Promise<MediaLinkPreview> {
	const { data } = await retry({
		task: async () =>
			fetch<PetitionDetailsMediaLinkPreviewQuery, PetitionDetailsMediaLinkPreviewQueryVariables>({
				query: PetitionDetailsMediaLinkPreview,
				variables: { input: { url } },
				rejectOnError: true,
			}),
		attempts: 3,
		delay: 250,
	});

	if (data.mediaLinkPreview.__typename === 'MediaLinkPreviewError') {
		throw new Error('media link preview fetch failed');
	}

	return data.mediaLinkPreview.preview;
}
